// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Realmfold — the 72 doublets, and the stratified draw that decides which of them a match plays with.
//
// THE DECK IS CONSTRUCTED, NOT AUTHORED. Seventy-two hand-written rows would be seventy-two chances
// to typo a census that nothing but the census test could catch; instead the *cell* population is
// declared once (TERRAINS below) and paired by a fixed, RNG-free algorithm. tests/deck.test.ts then
// asserts every number in that table independently — 144 cells, 42 coroneted cells, 60 coronets, the
// per-terrain split, exactly 18 same-terrain doublets, and at most one coroneted cell per doublet.
//
// THE NUMBER IS DERIVED, AND THE NUMBER IS THE WHOLE GAME:
//
//     power = 24·coronets + scarcity(a) + scarcity(b) + 2·sameTerrain
//
// Sorted ascending, that assigns 1..72, and because the largest possible blank power is 14 < 24 the
// coronet bands never interleave: 1–30 are blank, 31–57 carry one, 58–69 two, 70–72 three. Claiming
// a HIGHER number buys a better doublet and a LATER slot next round. That inversion is the game, so
// the ordering has to be a total order with no ties to break — hence unique integers rather than a
// power score, and hence the tie-break on (terrainA, terrainB, buildIndex) when powers collide.
//
// A doublet carries coronets on AT MOST ONE of its two cells. That is a legibility rule before it is
// a balance one: one badge to read at 48px, and a player can tell at a glance which half is the
// valuable one.

import { makeRng, shuffle, type Rng } from '@ben-gy/game-engine/rng';
import { TERRAIN_KEYS } from './palette';

export const TERRAIN_COUNT = 6;
export const WOLD = 0;
export const PINE = 1;
export const LOCH = 2;
export const REED = 3;
export const ORCH = 4;
export const COOMB = 5;

/** How rare a terrain is, and therefore how much of a doublet's pick-order price it pays. */
export const SCARCITY: readonly number[] = [0, 1, 2, 3, 4, 6];

interface TerrainSpec {
  /** Blank cells of this terrain. */
  blank: number;
  /** Coroneted cells, as [coronets, howMany] pairs. */
  crowned: ReadonlyArray<readonly [number, number]>;
  /** Same-terrain doublets made from this terrain: [blankPairs, crownedPairCoronets|0]. */
  sameBlank: number;
  sameCrowned: number;
}

/**
 * The cell population. Read the rows against plan.md's table — cells, coroneted cells and coronets
 * per terrain all come from here and nowhere else.
 *
 * The gradient is the point: Wold is 0.111 coronets per cell and Coomb is 1.417, a 12.8× spread over
 * six terrains. Twelve Coomb cells exist in the entire deck, which is few enough that a player can
 * literally count how many are left.
 */
const TERRAINS: readonly TerrainSpec[] = [
  // wold  — 36 cells, 4 coroneted, 4 coronets
  { blank: 32, crowned: [[1, 4]], sameBlank: 5, sameCrowned: 0 },
  // pine  — 30 cells, 6 coroneted, 6 coronets
  { blank: 24, crowned: [[1, 6]], sameBlank: 3, sameCrowned: 1 },
  // loch  — 27 cells, 7 coroneted, 8 coronets
  { blank: 20, crowned: [[1, 6], [2, 1]], sameBlank: 2, sameCrowned: 1 },
  // reed  — 21 cells, 8 coroneted, 11 coronets
  { blank: 13, crowned: [[1, 5], [2, 3]], sameBlank: 2, sameCrowned: 2 },
  // orch  — 18 cells, 9 coroneted, 14 coronets
  { blank: 9, crowned: [[1, 4], [2, 5]], sameBlank: 1, sameCrowned: 2 },
  // coomb — 12 cells, 8 coroneted, 17 coronets
  { blank: 4, crowned: [[1, 2], [2, 3], [3, 3]], sameBlank: 0, sameCrowned: 3 },
];

export interface Cell {
  terrain: number;
  coronets: number;
}

export interface Doublet {
  /** 1..72, ascending by power. Lower = weaker = you pick earlier next round. */
  n: number;
  a: Cell;
  b: Cell;
  /** Total coronets, 0..3. Carried on exactly one of the two cells when non-zero. */
  coronets: number;
  /** Both halves the same terrain — an instant two-cell region, and a colony founder in shire. */
  same: boolean;
}

/** A pile of identical cells, drained by take(). Keeps the pairing loop honest about supply. */
interface Pile {
  terrain: number;
  coronets: number;
  left: number;
}

function buildDoublets(): Doublet[] {
  const blanks: number[] = [];
  const crowned: Pile[] = [];
  for (let t = 0; t < TERRAIN_COUNT; t++) {
    blanks.push(TERRAINS[t].blank);
    for (const [coronets, count] of TERRAINS[t].crowned) crowned.push({ terrain: t, coronets, left: count });
  }

  const out: Array<{ a: Cell; b: Cell }> = [];
  const cell = (terrain: number, coronets: number): Cell => ({ terrain, coronets });

  // ── 1. the same-terrain doublets ────────────────────────────────────────────
  // Taken first because they are the scarcest resource in the deck (18 of 72) and the only pieces
  // that can found a colony in shire, so they must not be left to whatever the pairing happens to
  // produce. A crowned same-terrain doublet always spends the terrain's HIGHEST remaining coronet
  // cell, which is why #72 is Coomb★3 | Coomb.
  for (let t = 0; t < TERRAIN_COUNT; t++) {
    const spec = TERRAINS[t];
    for (let i = 0; i < spec.sameBlank; i++) {
      blanks[t] -= 2;
      out.push({ a: cell(t, 0), b: cell(t, 0) });
    }
    if (spec.sameCrowned > 0) {
      const pile = crowned.find((p) => p.terrain === t && p.coronets === spec.sameCrowned && p.left > 0);
      if (!pile) throw new Error(`deck: no ${spec.sameCrowned}-coronet ${TERRAIN_KEYS[t]} cell for its pair`);
      pile.left--;
      blanks[t] -= 1;
      out.push({ a: cell(t, spec.sameCrowned), b: cell(t, 0) });
    }
  }

  // ── 2. every remaining coroneted cell takes a blank of a DIFFERENT terrain ──
  // Always from the largest other pile: that levels the blanks down so step 3 can still pair what is
  // left into different-terrain doublets. Deterministic — largest pile, ties to the lowest terrain.
  const takeBlank = (notTerrain: number): number => {
    let best = -1;
    for (let t = 0; t < TERRAIN_COUNT; t++) {
      if (t === notTerrain || blanks[t] <= 0) continue;
      if (best < 0 || blanks[t] > blanks[best]) best = t;
    }
    if (best < 0) throw new Error(`deck: no blank cell left to partner terrain ${notTerrain}`);
    blanks[best]--;
    return best;
  };
  for (const pile of crowned) {
    while (pile.left > 0) {
      pile.left--;
      const partner = takeBlank(pile.terrain);
      out.push({ a: cell(pile.terrain, pile.coronets), b: cell(partner, 0) });
    }
  }

  // ── 3. the blanks that are left pair off, largest with second-largest ───────
  // That order is what makes the pairing possible at all: it succeeds exactly when no single
  // terrain holds more than half the remaining blanks, and always draining the two biggest piles
  // preserves that. A failure here is a deck-table bug, so it throws rather than degrading.
  for (;;) {
    let first = -1;
    let second = -1;
    for (let t = 0; t < TERRAIN_COUNT; t++) {
      if (blanks[t] <= 0) continue;
      if (first < 0 || blanks[t] > blanks[first]) {
        second = first;
        first = t;
      } else if (second < 0 || blanks[t] > blanks[second]) {
        second = t;
      }
    }
    if (first < 0) break;
    if (second < 0) throw new Error('deck: an odd blank cell of a single terrain is left over');
    blanks[first]--;
    blanks[second]--;
    out.push({ a: cell(first, 0), b: cell(second, 0) });
  }

  // ── 4. number them ──────────────────────────────────────────────────────────
  const scored = out.map((d, buildIndex) => {
    const coronets = d.a.coronets + d.b.coronets;
    const same = d.a.terrain === d.b.terrain;
    const power = 24 * coronets + SCARCITY[d.a.terrain] + SCARCITY[d.b.terrain] + (same ? 2 : 0);
    return { d, coronets, same, power, buildIndex };
  });
  scored.sort(
    (x, y) =>
      x.power - y.power ||
      Math.min(x.d.a.terrain, x.d.b.terrain) - Math.min(y.d.a.terrain, y.d.b.terrain) ||
      Math.max(x.d.a.terrain, x.d.b.terrain) - Math.max(y.d.a.terrain, y.d.b.terrain) ||
      x.buildIndex - y.buildIndex,
  );
  return scored.map((s, i) => ({
    n: i + 1,
    // The coroneted half is always `a`, so the renderer never has to ask which badge to draw where.
    a: s.d.a.coronets >= s.d.b.coronets ? s.d.a : s.d.b,
    b: s.d.a.coronets >= s.d.b.coronets ? s.d.b : s.d.a,
    coronets: s.coronets,
    same: s.same,
  }));
}

/** The whole deck, numbered 1..72. `DECK[i].n === i + 1`. */
export const DECK: readonly Doublet[] = buildDoublets();

export const DECK_SIZE = DECK.length;

/** Look a doublet up by its printed number. Returns null for anything off the wire that is not one. */
export function doubletOf(n: number): Doublet | null {
  return Number.isInteger(n) && n >= 1 && n <= DECK_SIZE ? DECK[n - 1] : null;
}

/**
 * The stratified pool draw — the single best fairness lever in the design.
 *
 * Split the number-sorted 72 into `n` contiguous buckets and take exactly one from each. Every match
 * therefore spans the identical power range in identical proportion; only the identities and the
 * order vary. A uniform draw would let one match hold four 3-coronet doublets and the next hold
 * none, and no amount of turn-order fairness survives that.
 */
export function stratifiedPool(seed: number, n: number): number[] {
  if (n > DECK_SIZE) throw new Error(`pool of ${n} exceeds the ${DECK_SIZE}-doublet deck`);
  const rng = makeRng(seed >>> 0);
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    // Contiguous buckets with the remainder given to the LOW buckets, so the cheap end of the deck
    // is the one that gets the extra choice rather than the expensive end.
    const lo = Math.floor((i * DECK_SIZE) / n);
    const hi = Math.floor(((i + 1) * DECK_SIZE) / n);
    out.push(lo + Math.floor(rng() * (hi - lo)) + 1);
  }
  return out;
}

/** A seeded shuffle, exposed so rules.ts and the tests share one implementation. */
export function shuffled<T>(rng: Rng, arr: readonly T[]): T[] {
  return shuffle(rng, arr);
}
